import { useAuth0 } from "@auth0/auth0-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { 
  getMyPlanSummary, 
  UserPlanSummary 
} from "../services/usage.service";

// Query keys
export const usageKeys = {
  all: ['usage'] as const,
  myPlan: () => [...usageKeys.all, 'myPlan'] as const,
} as const;

// Default configuration
const DEFAULT_STALE_TIME = 2 * 60 * 1000; // 2 minutes
const DEFAULT_CACHE_TIME = 5 * 60 * 1000; // 5 minutes

/**
 * Hook to fetch the current user's plan and usage summary
 */
export const useMyPlanSummary = (options?: { enabled?: boolean }) => {
  const { getAccessTokenSilently } = useAuth0();
  
  return useQuery<UserPlanSummary, Error>({
    queryKey: usageKeys.myPlan(),
    queryFn: async () => {
      const accessToken = await getAccessTokenSilently();
      const response = await getMyPlanSummary(accessToken);
      
      if (response.error) {
        throw new Error(response.error);
      }
      
      if (!response.data) {
        throw new Error("No data returned from the server");
      }
      
      return response.data;
    },
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_CACHE_TIME,
    ...options
  });
};

/**
 * Hook to get a function that can invalidate usage queries
 */
export const useInvalidateUsageQueries = () => {
  const queryClient = useQueryClient();
  
  return () => {
    // Invalidate and refetch usage data
    queryClient.invalidateQueries({ queryKey: usageKeys.myPlan(), refetchType: 'all' });
    queryClient.refetchQueries({ queryKey: usageKeys.myPlan(), type: 'active' });
  };
};
